import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { listDevelopers, createDeveloper, archiveDeveloper, isLegacyCompany, type PlatformDeveloper } from '../../lib/platform-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

export function PlatformDevelopersPage() {
  const t = useT();
  const location = useLocation();
  const [devs, setDevs] = useState<PlatformDeveloper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newDev, setNewDev] = useState(false);
  const [archiving, setArchiving] = useState<PlatformDeveloper | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setDevs(await listDevelopers());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const focus = new URLSearchParams(location.search).get('focus');
    if (focus && devs.some((developer) => developer.id === focus)) {
      document.getElementById(`developer-${focus}`)?.scrollIntoView({ block: 'center' });
    }
  }, [devs, location.search]);

  const activeDevs = devs.filter((d) => !d.archived_at && !isLegacyCompany(d.id));
  const legacyProperties = devs.find((d) => isLegacyCompany(d.id))?.clusters
    .filter((cluster) => !cluster.archived_at) ?? [];

  async function confirmArchive() {
    if (!archiving) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      await archiveDeveloper(archiving.id);
      setArchiving(null);
      refresh();
    } catch (e) {
      setArchiveError(extractApiError(e).message);
    } finally {
      setArchiveBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="h4" fontWeight={600}>{t('plt.devTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('plt.devSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewDev(true)}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('plt.newDev')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {legacyProperties.length > 0 ? <Alert severity="warning">
        {t('plt.legacyHelp')} {legacyProperties.map((cluster) => cluster.name).join(', ')}.
      </Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : activeDevs.length === 0 ? (
        <Card variant="outlined"><CardContent>
          <Typography color="text.secondary">{t('plt.noDevYet')}</Typography>
        </CardContent></Card>
      ) : (
        activeDevs.map((d) => (
          <Card key={d.id} id={`developer-${d.id}`} variant="outlined"
            sx={new URLSearchParams(location.search).get('focus') === d.id ? { borderColor: 'primary.main' } : undefined}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6">{d.name}</Typography>
                <Chip size="small" label={d.status} color={d.status === 'ACTIVE' ? 'success' : 'default'} />
                <Typography variant="caption" color="text.secondary">/{d.slug}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title={t('plt.archive')}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => { setArchiveError(null); setArchiving(d); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box sx={{ mt: 1 }}>
                {d.clusters.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('plt.noClusters')}</Typography>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {d.clusters.map((cl) => (
                      <Chip key={cl.id} label={cl.name} variant="outlined" size="small" />
                    ))}
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      <CreateDeveloperDialog
        open={newDev}
        onClose={() => setNewDev(false)}
        onCreated={() => { setNewDev(false); refresh(); }}
      />

      {/* Archive confirm dialog */}
      <Dialog open={Boolean(archiving)} onClose={() => !archiveBusy && setArchiving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('plt.archiveDevTitle', { name: archiving?.name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('plt.archiveDevWarning')}
            </Typography>
            {archiveError ? <Alert severity="error">{archiveError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiving(null)} disabled={archiveBusy}>{t('common.cancel')}</Button>
          <Button onClick={confirmArchive} variant="contained" color="error" disabled={archiveBusy}>
            {archiveBusy ? t('plt.archiving') : t('plt.archive')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function CreateDeveloperDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void;
}) {
  const t = useT();
  const [developerName, setDeveloperName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDeveloperName('');
      setError(null);
    }
  }, [open]);

  async function save() {
    setBusy(true); setError(null);
    try { await createDeveloper({ developer_name: developerName.trim() }); onCreated(); }
    catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plt.newDevTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('plt.newDevNote')}
          </Typography>
          <TextField label={t('plt.devNameField')} value={developerName} onChange={(e) => setDeveloperName(e.target.value)} required fullWidth />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={save} variant="contained" disabled={!developerName.trim() || busy}>{busy ? t('common.saving') : t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
